import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';
import { render } from 'react-dom';
import FocusTrap from 'focus-trap-react';
import { Router } from 'react-router-dom';
import { Provider } from 'react-reqq-lite';
import { IoClose } from 'react-icons/io5';

import store from 'setup/store';
import history from 'setup/history';

const LightBoxMarkerModal = React.forwardRef((props, ref) => {
  const { opt, onClose } = props;
  const [animate, setAnimate] = React.useState(false);

  React.useEffect(() => {
    setAnimate(true);
  }, []);

  const escFunction = (e) => {
    if (e.keyCode === 27) {
      setAnimate(false);
      onClose();
    }
  };

  const handleOnClose = () => {
    setAnimate(false);
    onClose();
  };

  document.addEventListener('keydown', escFunction, false);

  const renderContent = () => {
    if (typeof opt.content === 'function') {
      return (
        <div className={cn('flex flex-col flex-1', opt?.contentClassName)}>
          {opt.content(onClose)}
        </div>
      );
    }

    if (typeof opt.content === 'string') {
      return (
        <div className={cn('flex flex-col flex-1', opt?.contentClassName)}>
          {opt.content}
        </div>
      );
    }

    return 'n/a';
  };

  return (
    <div className="flex flex-col fixed z-50 inset-0 overflow-y-auto" ref={ref}>
      <div
        className={cn(
          'fixed inset-0 bg-black bg-opacity-70 transition-opacity',
          {
            'opacity-100 ease-in-out duration-300': animate,
            'opacity-0 ease-in-out duration-200': !animate,
          }
        )}
      />

      <div
        className={cn(
          'flex justify-center items-center w-full p-3 md:p-5 flex-1',
          opt?.wrapperClassName
        )}
      >
        <div className="fixed top-2.5 right-2.5 md:top-3 md:right-4">
          <IoClose
            className="h-ds-24 w-ds-24 text-white cursor-pointer"
            onClick={handleOnClose}
          />
        </div>

        <div
          className={cn('flex flex-col w-full z-10 transition-opacity', {
            'opacity-0 ease-in-out duration-75': !animate,
          })}
        >
          {opt.focusTrap ? (
            <FocusTrap
              focusTrapOptions={{
                allowOutsideClick: () => true,
              }}
            >
              {renderContent()}
            </FocusTrap>
          ) : (
            renderContent()
          )}
        </div>
      </div>
    </div>
  );
});

LightBoxMarkerModal.propTypes = {
  opt: PropTypes.instanceOf(Object).isRequired,
  onClose: PropTypes.instanceOf(Function).isRequired,
};

export const showLightBox = (options) => {
  const opt = {
    title: false,
    focusTrap: false,
    modalSize: 'modal-xs',
    escButton: false,
    titleClassName: '',
    align: 'justify-center flex items-center',
    content: 'Confirmation Message',
    containerClassName: '',
    contentClassName: '',
    wrapperClassName: '',
    barContent: false,
    barProps: {},
    ...options,
  };

  const prevElem = document.activeElement;

  if (document.body.scrollHeight > document.body.clientHeight) {
    document.body.className = 'overflow-hidden mr-[7px]';
  } else {
    document.body.className = 'overflow-hidden';
  }

  const root = document.getElementById('light-box-root');

  const onClose = () => {
    try {
      if (opt.onCloseCallback) {
        opt.onCloseCallback();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err, 'Light Box Marker Error!!');
    }
    setTimeout(() => {
      document.body.removeAttribute('class');
      render(<div id="light-box-root" />, root);
      setTimeout(() => {
        try {
          prevElem.blur();
        } catch (err) {} // eslint-disable-line
      }, 100);
    }, 200);
  };

  render(
    <Provider store={store}>
      <Router history={history}>
        <LightBoxMarkerModal opt={opt} onClose={onClose} />
      </Router>
    </Provider>,
    root
  );
};

const LightBoxMarker = () => <div id="light-box-root" />;

export default LightBoxMarker;
